import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import { DarkTableContainer, TabContentWrapper } from '../styles/TournamentStyles';
import Maps from './Maps';
import RulesTab from './tournament/RulesTab';
import { MODES, DEFAULT_MODE, modeLabel } from '../../constants/modes';

const TeamTournamentView = ({
  teams,
  rounds,
  activeTab,
  tournamentId,
  isMobile,
  tournament,
  players,
}) => {
  const [mode, setMode] = useState(DEFAULT_MODE);

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  // Get rounds for the current mode
  const filteredRounds = rounds.filter((round) => round.mode === mode);

  const renderTeamStandings = () => {
    return (
      <Box>
        {/* Team Total Standings */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{ mb: 2, color: 'white', fontSize: { xs: '1.3rem', sm: '1.5rem' } }}
          >
            Team Standings
          </Typography>
          <DarkTableContainer component={Paper}>
            <Table style={{ tableLayout: 'fixed', borderCollapse: 'unset' }}>
              <TableHead>
                <TableRow>
                  <TableCell className="position-column">Position</TableCell>
                  <TableCell className="standard-column">Team</TableCell>
                  <TableCell className="standard-column">Total Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team, index) => (
                  <TableRow key={team.id}>
                    <TableCell className="position-column">{index + 1}</TableCell>
                    <TableCell className="standard-column">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {team.imageUrl && (
                          <Box
                            component="img"
                            src={team.imageUrl}
                            alt={team.name}
                            sx={{
                              width: { xs: 20, sm: 30 },
                              height: { xs: 20, sm: 30 },
                              objectFit: 'contain',
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            width: { xs: 12, sm: 16 },
                            height: { xs: 12, sm: 16 },
                            borderRadius: '50%',
                            bgcolor: team.color || '#000',
                          }}
                        />
                        <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                          {team.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell className="standard-column">{team.totalPoints || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DarkTableContainer>
        </Box>

        {/* Current Mode Rounds */}
        {filteredRounds.length > 0 ? (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{ mb: 2, color: 'white', fontSize: { xs: '1.3rem', sm: '1.5rem' } }}
            >
              {mode} Rounds
            </Typography>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                sx={{ bgcolor: 'white' }}
              >
                {MODES.map((modeOption) => (
                  <ToggleButton key={modeOption} value={modeOption}>
                    {modeLabel(modeOption)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {filteredRounds.map((round) => (
              <Box key={round.id} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  color="white"
                  sx={{ mb: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  {round.name}
                </Typography>

                {round.mapName && (
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mb: 1 }}>
                    Map: {round.mapName}
                  </Typography>
                )}

                <DarkTableContainer component={Paper}>
                  <Table size="small" style={{ tableLayout: 'fixed', borderCollapse: 'unset' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className="position-column">Position</TableCell>
                        <TableCell className="standard-column">Team</TableCell>
                        <TableCell className="standard-column">Player</TableCell>
                        <TableCell className="standard-column">Points</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {round.results
                        .sort((a, b) => a.position - b.position)
                        .map((result) => (
                          <TableRow key={result.playerId}>
                            <TableCell className="position-column">{result.position}</TableCell>
                            <TableCell className="standard-column">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: { xs: 10, sm: 12 },
                                    height: { xs: 10, sm: 12 },
                                    borderRadius: '50%',
                                    bgcolor: result.teamColor || '#000',
                                  }}
                                />
                                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  {result.teamName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="standard-column">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {result.country && (
                                  <img
                                    src={`https://flagcdn.com/16x12/${result.country.toLowerCase()}.png`}
                                    alt={result.countryName || ''}
                                    style={{ width: 16, height: 12 }}
                                  />
                                )}
                                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  {result.playerName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="standard-column">{result.points || 0}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </DarkTableContainer>
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info" sx={{ bgcolor: '#282846', color: 'white' }}>
            No {mode} rounds available for this tournament.
          </Alert>
        )}
      </Box>
    );
  };

  // Team player details
  const renderTeamPlayers = () => {
    return (
      <Grid container spacing={2}>
        {teams.map((team) => (
          <Grid item xs={12} key={team.id}>
            <Card sx={{ bgcolor: '#1a1a2e', color: 'white', mb: 2 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {team.imageUrl && (
                    <Avatar
                      src={team.imageUrl}
                      sx={{ width: { xs: 40, sm: 50 }, height: { xs: 40, sm: 50 } }}
                    />
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                      {team.name}
                    </Typography>
                    <Box
                      sx={{
                        width: { xs: 16, sm: 20 },
                        height: { xs: 16, sm: 20 },
                        borderRadius: '50%',
                        bgcolor: team.color || '#000',
                        display: 'inline-block',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      }}
                    >
                      Total Points: {team.totalPoints || 0}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

                <DarkTableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small" style={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className="standard-column">Player</TableCell>
                        <TableCell className="standard-column">Mode</TableCell>
                        <TableCell className="standard-column">Country</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {team.members?.map((player) => (
                        <TableRow key={player.id}>
                          <TableCell className="standard-column">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {player.imageUrl && (
                                <Avatar
                                  src={player.imageUrl}
                                  sx={{ width: { xs: 24, sm: 30 }, height: { xs: 24, sm: 30 } }}
                                />
                              )}
                              <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                {player.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell className="standard-column">
                            <Chip
                              label={player.mode}
                              size="small"
                              color="primary"
                              sx={{
                                height: { xs: 18, sm: 20 },
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              }}
                            />
                          </TableCell>
                          <TableCell className="standard-column">
                            {player.country && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img
                                  src={`https://flagcdn.com/24x18/${player.country.toLowerCase()}.png`}
                                  alt={player.countryName}
                                  style={{ width: 24, height: 18 }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    display: { xs: 'none', sm: 'block' },
                                  }}
                                >
                                  {player.countryName}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DarkTableContainer>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <>
      {activeTab === 0 && <TabContentWrapper>{renderTeamStandings()}</TabContentWrapper>}

      {activeTab === 1 && (
        <TabContentWrapper noshadow="true">
          <Maps tournamentId={tournamentId} />
        </TabContentWrapper>
      )}

      {activeTab === 2 && (
        <TabContentWrapper>
          <RulesTab html={tournament.rules} />
        </TabContentWrapper>
      )}

      {activeTab === 3 && <TabContentWrapper>{renderTeamPlayers()}</TabContentWrapper>}
    </>
  );
};

export default TeamTournamentView;
